import { computed, onScopeDispose, reactive, ref, unref } from "vue";
import type { ComputedRef, Ref } from "vue";

import { masterServiceApi } from "@/entities/master-service";
import { serviceApi } from "@/entities/service";
import type { AppError } from "@/shared/api";
import { isAppError } from "@/shared/api";
import { i18n } from "@/shared/i18n";
import { parseAmountToCents, toast } from "@/shared/lib";

import {
  mapMasterServiceError,
  validateServiceForm,
} from "@/entities/master-service";
import type { MasterServiceI18nKey } from "@/entities/master-service";

import type { AssignedRow } from "./types";

function tr(key: MasterServiceI18nKey): string {
  return i18n.global.t(key);
}

export interface UseMasterServicesOptions {
  onNotFound: () => void
}

export interface UseMasterServicesResult {
  listStatus: Ref<"loading" | "ready" | "error">
  assigned: Ref<AssignedRow[]>
  available: ComputedRef<{ value: string; label: string }[]>
  catalogEmpty: ComputedRef<boolean>
  politeMessage: Ref<string>
  form: {
    open: boolean
    mode: "add" | "edit"
    serviceId: string | null
    duration: string
    price: string
    buffer: string
    status: "idle" | "saving"
    fieldErrors: Partial<Record<"service" | "duration" | "price" | "buffer", string>>
    formError?: string
  }
  detach: {
    open: boolean
    serviceId: string | null
    status: "idle" | "removing"
    error?: string
  }
  load(): Promise<void>
  openAdd(): void
  openEdit(serviceId: string): void
  closeForm(): void
  submitForm(): Promise<void>
  requestDetach(serviceId: string): void
  cancelDetach(): void
  confirmDetach(): Promise<void>
}

/**
 * Composable для екранного стану «послуги майстра» (spec feature-064 T3).
 * Не є Pinia-стором — кожен маунт отримує чистий інстанс.
 * `canceled`-прапор захищає від stale-write після unmount.
 * AbortController не використовується: entity-API не приймає signal (tech-068).
 */
export function useMasterServices(
  masterId: string | Ref<string>,
  opts: UseMasterServicesOptions,
): UseMasterServicesResult {
  const listStatus = ref<"loading" | "ready" | "error">("loading");
  const assigned = ref<AssignedRow[]>([]);
  /** Повний каталог (internal) — не виставляємо назовні. */
  const catalog = ref<{ id: string; name: string }[]>([]);

  const available = computed(() => {
    const linkedIds = new Set(assigned.value.map((r) => r.serviceId));
    return catalog.value
      .filter((s) => !linkedIds.has(s.id))
      .map((s) => ({ value: s.id, label: s.name }));
  });

  const catalogEmpty = computed(() => catalog.value.length === 0);
  const politeMessage = ref<string>("");

  const form = reactive({
    open: false,
    mode: "add" as "add" | "edit",
    serviceId: null as string | null,
    duration: "",
    price: "",
    buffer: "",
    status: "idle" as "idle" | "saving",
    fieldErrors: {} as Partial<Record<"service" | "duration" | "price" | "buffer", string>>,
    formError: undefined as string | undefined,
  });

  const detach = reactive({
    open: false,
    serviceId: null as string | null,
    status: "idle" as "idle" | "removing",
    error: undefined as string | undefined,
  });

  let canceled = false;

  // ---------------------------------------------------------------------------
  // load
  // ---------------------------------------------------------------------------

  async function load(): Promise<void> {
    canceled = false;
    listStatus.value = "loading";

    try {
      const id = unref(masterId);
      const [linked, services] = await Promise.all([
        masterServiceApi.listMasterServices(id),
        serviceApi.listServices(),
      ]);

      if (canceled) return;

      catalog.value = services;

      const catalogById = new Map(services.map((s) => [s.id, s.name]));
      assigned.value = linked.map((item) => ({
        serviceId: item.serviceId,
        name: catalogById.get(item.serviceId) ?? tr("masters.services.unknownService"),
        durationMin: item.durationMin,
        priceCents: item.priceCents,
        bufferMin: item.bufferMin,
      }));

      listStatus.value = "ready";
    } catch (err) {
      if (canceled) return;

      const appErr = err as AppError;
      if (appErr.kind === "notFound") {
        opts.onNotFound();
        return;
      }
      listStatus.value = "error";
    }
  }

  // ---------------------------------------------------------------------------
  // Form actions
  // ---------------------------------------------------------------------------

  function openAdd(): void {
    form.open = true;
    form.mode = "add";
    form.serviceId = null;
    form.duration = "";
    form.price = "";
    form.buffer = "";
    form.status = "idle";
    form.fieldErrors = {};
    form.formError = undefined;
  }

  function openEdit(serviceId: string): void {
    const row = assigned.value.find((r) => r.serviceId === serviceId);
    if (!row) return;

    form.open = true;
    form.mode = "edit";
    form.serviceId = serviceId;
    form.duration = String(row.durationMin);
    form.price = (row.priceCents / 100).toFixed(2);
    form.buffer = String(row.bufferMin);
    form.status = "idle";
    form.fieldErrors = {};
    form.formError = undefined;
  }

  function closeForm(): void {
    form.open = false;
    form.mode = "add";
    form.serviceId = null;
    form.duration = "";
    form.price = "";
    form.buffer = "";
    form.status = "idle";
    form.fieldErrors = {};
    form.formError = undefined;
  }

  async function submitForm(): Promise<void> {
    if (form.status === "saving") return;

    form.status = "saving";
    form.fieldErrors = {};
    form.formError = undefined;

    const fieldErrors = validateServiceForm(
      {
        serviceId: form.serviceId,
        duration: form.duration,
        price: form.price,
        buffer: form.buffer,
      },
      form.mode,
    );

    if (Object.keys(fieldErrors).length > 0) {
      form.fieldErrors = fieldErrors;
      form.status = "idle";
      return;
    }

    const durationMin = parseInt(form.duration, 10);
    const rawCents = parseAmountToCents(form.price);
    const priceCents = isNaN(rawCents) ? 0 : rawCents;
    const bufferMin = form.buffer === "" ? 0 : parseInt(form.buffer, 10);

    try {
      const result = await masterServiceApi.setMasterService(
        unref(masterId),
        form.serviceId!,
        { durationMin, priceCents, bufferMin },
      );

      if (canceled) return;

      const catalogEntry = catalog.value.find((s) => s.id === result.serviceId);
      const updatedRow: AssignedRow = {
        serviceId: result.serviceId,
        name: catalogEntry?.name ?? tr("masters.services.unknownService"),
        durationMin: result.durationMin,
        priceCents: result.priceCents,
        bufferMin: result.bufferMin,
      };

      const idx = assigned.value.findIndex((r) => r.serviceId === result.serviceId);
      if (idx >= 0) {
        assigned.value[idx] = updatedRow;
      } else {
        assigned.value.push(updatedRow);
      }

      politeMessage.value = tr("masters.services.saved");
      form.open = false;
    } catch (err) {
      if (canceled) return;

      const appErr: AppError = isAppError(err) ? err : { kind: "server", retriable: true };
      const mapped = mapMasterServiceError(appErr, "submit");

      switch (mapped.surface) {
        case "notFound":
          opts.onNotFound();
          break;
        case "stale":
          form.formError = mapped.i18nKey;
          void load();
          break;
        case "field":
          if (mapped.field) {
            form.fieldErrors[mapped.field] = mapped.i18nKey;
          }
          break;
        case "toast":
          toast.error(tr(mapped.i18nKey));
          break;
        default:
          form.formError = mapped.i18nKey;
      }
    } finally {
      form.status = "idle";
    }
  }

  // ---------------------------------------------------------------------------
  // Detach actions
  // ---------------------------------------------------------------------------

  function requestDetach(serviceId: string): void {
    detach.open = true;
    detach.serviceId = serviceId;
    detach.status = "idle";
    detach.error = undefined;
  }

  function cancelDetach(): void {
    detach.open = false;
  }

  async function confirmDetach(): Promise<void> {
    if (detach.status === "removing") return;

    detach.status = "removing";

    try {
      await masterServiceApi.removeMasterService(unref(masterId), detach.serviceId!);

      if (canceled) return;

      const idx = assigned.value.findIndex((r) => r.serviceId === detach.serviceId);
      if (idx >= 0) {
        assigned.value.splice(idx, 1);
      }

      politeMessage.value = tr("masters.services.removed");
      detach.open = false;
    } catch (err) {
      if (canceled) return;

      const appErr: AppError = isAppError(err) ? err : { kind: "server", retriable: true };
      const mapped = mapMasterServiceError(appErr, "detach");

      switch (mapped.surface) {
        case "stale": {
          const idx = assigned.value.findIndex((r) => r.serviceId === detach.serviceId);
          if (idx >= 0) {
            assigned.value.splice(idx, 1);
          }
          detach.error = mapped.i18nKey;
          break;
        }
        case "notFound":
          opts.onNotFound();
          break;
        case "toast":
          toast.error(tr(mapped.i18nKey));
          break;
        default:
          detach.error = mapped.i18nKey;
      }
    } finally {
      detach.status = "idle";
    }
  }

  // ---------------------------------------------------------------------------
  // Cleanup
  // ---------------------------------------------------------------------------

  onScopeDispose(() => {
    canceled = true;
  });

  return {
    listStatus,
    assigned,
    available,
    catalogEmpty,
    politeMessage,
    form,
    detach,
    load,
    openAdd,
    openEdit,
    closeForm,
    submitForm,
    requestDetach,
    cancelDetach,
    confirmDetach,
  };
}
